import type { FC, ReactNode } from 'react'
import type { ListingCard as ListingCardData } from '@/types/marketplace'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { ListingGrid } from '@/components/marketplace/listing-card'

interface FeaturedListingsProps {
    listings: Array<ListingCardData>
    /** Where "See all featured" lands. The browse surface already filters on it. */
    href?: string
    className?: string
    /** The storefront these cards link within. Empty for the founding one. */
    basePath?: string
}

/** The row is three across on a laptop, two on a tablet, one on a phone. */
const MAX_CARDS = 3

export const FeaturedListings: FC<FeaturedListingsProps> = ({
    listings,
    href = '/browse?featured=1',
    className,
    basePath = ''
}): ReactNode => {
    if (listings.length === 0) return null

    return (
        <div className={cn('relative', className)}>
            {/* Hand-drawn marks live behind everything and never take a click. */}
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 z-0 select-none'>
                <StarBurst className='absolute right-1 top-[6.5rem] hidden h-9 w-9 text-ink-900 sm:block lg:right-2 lg:top-[7.5rem]' />
            </div>

            <div className='relative z-10'>
                <div className='flex flex-wrap items-start justify-between gap-x-6 gap-y-4'>
                    <div className='max-w-2xl'>
                        <h2 className='flex items-start gap-2 sm:gap-3'>
                            <span className='type-display text-[2rem] leading-[0.9] text-ink-900 sm:text-[2.75rem]'>
                                {'Featured '}

                                <span className='text-brand-red'>
                                    Listings
                                </span>
                            </span>

                            <Crown className='mt-0.5 h-5 w-7 shrink-0 text-ink-900 sm:h-7 sm:w-10' />
                        </h2>

                        <p className='mt-2.5 text-sm text-ink-900/75 sm:text-[15px]'>
                            Promoted placement, chosen by our team for a set period. Always labelled.
                        </p>

                        <HandUnderline className='mt-2 h-2 w-44 sm:w-52' />
                    </div>

                    <Link
                        href={href}
                        className='mta-focus group inline-flex min-h-11 items-center gap-3 self-start rounded-mta-xs font-heading text-sm font-bold text-ink-900 transition-colors duration-200 hover:text-brand-red-ink sm:mt-2 sm:text-base'
                    >
                        See all featured

                        <ArrowRight
                            className='size-4 transition-transform duration-200 ease-out motion-safe:group-hover:translate-x-1'
                            aria-hidden='true'
                        />
                    </Link>
                </div>

                <ListingGrid
                    listings={listings
                        .slice(0, MAX_CARDS)
                        .map((listing) => ({ ...listing, isFeatured: true }))}
                    priorityCount={1}
                    basePath={basePath}
                    className='mt-7 sm:mt-10 lg:grid-cols-3 xl:grid-cols-3'
                />
            </div>
        </div>
    )
}

/** The small hand-drawn crown that marks the section title. Decorative only. */
const Crown: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <svg
            viewBox='0 0 64 44'
            className={className}
            fill='none'
            stroke='currentColor'
            strokeWidth='4'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
            focusable='false'
        >
            <path d='M5 39C7.2 27.4 6.6 15.8 4 5.5c6.4 6.8 12.4 12 18.4 15.6C25.6 15 28.6 9.4 31.8 3.4c3.6 6.4 6.8 12.2 9.8 17.4C47.4 17.6 53.2 12.6 59.6 6c-2.4 10.6-3 22.2-1 33' />
            <circle cx='60.4' cy='41.2' r='2.2' fill='currentColor' stroke='none' />
        </svg>
    )
}

/** The small hand-drawn sparkle at the far right edge, above the grid. */
const StarBurst: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <svg
            viewBox='0 0 48 48'
            className={className}
            fill='none'
            stroke='currentColor'
            strokeWidth='2.4'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
            focusable='false'
        >
            <path d='M24 2.5 27.6 18 43 12.6 30.6 23.4 45.5 30 29.4 29.2 33.6 45.5 24 32.2 14.4 45.5 18.6 29.2 2.5 30 17.4 23.4 5 12.6 20.4 18Z' />
        </svg>
    )
}

/** The rough red rule under the section's supporting line. */
const HandUnderline: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <svg
            viewBox='0 0 140 10'
            preserveAspectRatio='none'
            className={cn('text-brand-red', className)}
            fill='none'
            stroke='currentColor'
            strokeWidth='3'
            strokeLinecap='round'
            aria-hidden='true'
            focusable='false'
        >
            <path d='M3 7.2C28 3.6 56 2.6 84 4.2c17 1 34 2 53 1.8' />
        </svg>
    )
}
